import { classifySaveFailure } from "./capacity.js";
import { sha256, sha256Json } from "./hash.js";
import {
  DEFAULT_STORAGE_LIMITS,
  MAX_CHECKPOINT_NAME_LENGTH,
  type NamedCheckpoint,
  type SaveAttempt,
  type RecoveryReport,
  type RecoveryCandidate,
  type StorageTransaction,
  StorageConflictError,
  StorageIntegrityError,
  StorageValidationError,
  type AssetInput,
  type DocumentCheckpoint,
  type LibraryQuery,
  type ProjectMetadataInput,
  type ProjectRecord,
  type ProjectSnapshot,
  type SaveCheckpointInput,
  type StorageAdapter,
  type StorageLimits,
  type StoredAsset,
} from "./types.js";
import { assertId, isUint8Array, normalizeSearch, validateJsonDocument, validateMetadata } from "./validation.js";

const checkpointKey = (projectId: string, revision: number): string => `${projectId}:${revision.toString().padStart(16, "0")}`;

export class LocalProjectStore {
  readonly #adapter: StorageAdapter;
  readonly #limits: StorageLimits;

  constructor(adapter: StorageAdapter, limits: StorageLimits = DEFAULT_STORAGE_LIMITS) {
    this.#adapter = adapter;
    this.#limits = { ...limits };
  }

  async hasProject(projectId: string): Promise<boolean> {
    assertId(projectId, "projectId");
    return this.#adapter.transaction(["projects"], "readonly", async (transaction) =>
      Boolean(await transaction.get<ProjectRecord>("projects", projectId)),
    );
  }

  /** Assets, checkpoint, and the head pointer are committed in one atomic transaction. */
  async saveCheckpoint(input: SaveCheckpointInput): Promise<ProjectSnapshot> {
    return this.#saveCheckpoint(input);
  }

  async #saveCheckpoint(source: SaveCheckpointInput, preserved?: NamedCheckpoint): Promise<ProjectSnapshot> {
    // Clone byte views explicitly: some browser/test realms do not preserve
    // Uint8Array identity when structured-cloning a containing object.
    const input: SaveCheckpointInput = {
      ...source,
      metadata: structuredClone(source.metadata),
      document: structuredClone(source.document),
      assetIds: [...source.assetIds],
      ...(source.assets === undefined ? {} : {
        assets: source.assets.map((asset) => ({ ...asset, bytes: new Uint8Array(asset.bytes) })),
      }),
      ...(source.now === undefined ? {} : { now: new Date(source.now) }),
    };
    assertId(input.projectId, "projectId");
    if (!Number.isSafeInteger(input.revision) || input.revision < 1) {
      throw new StorageValidationError("revision must be a positive safe integer");
    }
    if (input.expectedHeadRevision !== undefined && (!Number.isSafeInteger(input.expectedHeadRevision) || input.expectedHeadRevision < 1)) throw new StorageValidationError("expectedHeadRevision must be a positive safe integer");
    const name = input.checkpointName === undefined ? undefined : validateCheckpointName(input.checkpointName);
    const metadata = validateMetadata(input.metadata, this.#limits);
    validateJsonDocument(input.document, this.#limits);
    if (input.assetIds.length > this.#limits.maxAssetsPerProject) {
      throw new StorageValidationError("Project references too many assets");
    }
    const assetIds = [...new Set(input.assetIds)];
    for (const assetId of assetIds) assertId(assetId, "assetId");
    const preparedAssets = await Promise.all((input.assets ?? []).map((asset) => this.#prepareAsset(asset, input.now)));
    const suppliedBytes = preparedAssets.reduce((sum, asset) => sum + asset.byteLength, 0);
    if (suppliedBytes > this.#limits.maxProjectBytes) throw new StorageValidationError("Project assets exceed the size limit");
    const createdAt = (input.now ?? new Date()).toISOString();
    const document = structuredClone(input.document);
    const documentHash = await sha256Json(document);
    const checkpoint: DocumentCheckpoint = {
      key: checkpointKey(input.projectId, input.revision),
      projectId: input.projectId,
      revision: input.revision,
      createdAt,
      documentHash,
      document,
      assetIds,
      complete: true,
      ...(name === undefined ? {} : { name }),
    };

    const attempt: SaveAttempt = {
      id: input.attemptId ?? globalThis.crypto.randomUUID(), projectId: input.projectId, revision: input.revision,
      documentHash, startedAt: createdAt, status: "writing",
      ...(input.expectedHeadRevision === undefined ? {} : { expectedHeadRevision: input.expectedHeadRevision }),
    };
    assertId(attempt.id, "attemptId");
    // A separate committed marker survives a process interruption of the document transaction.
    await this.#adapter.transaction(["save-attempts"], "readwrite", async (transaction) => {
      if (await transaction.get("save-attempts", attempt.id)) throw new StorageConflictError("Save attempt ID already exists");
      await transaction.put("save-attempts", attempt.id, attempt);
    });
    try {
      return await this.#adapter.transaction(
        ["assets", "checkpoints", "projects", "save-attempts", "named-checkpoints"],
        "readwrite",
        async (transaction) => {
          const existing = await transaction.get<ProjectRecord>("projects", input.projectId);
          if (input.createOnly && existing) throw new StorageConflictError("Project copy ID already exists");
          if (input.expectedHeadRevision !== undefined && existing?.headRevision !== input.expectedHeadRevision) {
            throw new StorageConflictError(
              `Expected head ${input.expectedHeadRevision}, found ${existing?.headRevision ?? "no project"}`,
            );
          }
          if (existing && input.expectedHeadRevision === undefined) throw new StorageConflictError("Saving an existing project requires expectedHeadRevision");
          if (existing?.deletedAt) throw new StorageConflictError("Project is in Trash; restore it before saving");
          if (existing && input.revision <= existing.headRevision) {
            throw new StorageConflictError("A checkpoint revision must advance the current head");
          }
          if (await transaction.get("checkpoints", checkpoint.key)) throw new StorageConflictError("Checkpoint revision already exists");
          for (const asset of preparedAssets) {
            const prior = await transaction.get<StoredAsset>("assets", asset.id);
            if (prior && prior.sha256 !== asset.sha256) {
              throw new StorageIntegrityError(`Asset ID ${asset.id} already has different content`);
            }
            if (!prior) await transaction.put("assets", asset.id, asset);
          }
          const resolvedAssets: StoredAsset[] = [];
          let totalBytes = 0;
          for (const assetId of assetIds) {
            const asset = await transaction.get<StoredAsset>("assets", assetId);
            if (!asset) throw new StorageIntegrityError(`Missing required asset: ${assetId}`);
            totalBytes += asset.byteLength;
            if (totalBytes > this.#limits.maxProjectBytes) throw new StorageValidationError("Project assets exceed the size limit");
            resolvedAssets.push(asset);
          }
          if (preserved) await putNamedCheckpoint(transaction, preserved);
          await transaction.put("checkpoints", checkpoint.key, checkpoint);
          const project: ProjectRecord = {
            id: input.projectId,
            title: metadata.title,
            normalizedTitle: normalizeSearch(metadata.title),
            tags: [...metadata.tags],
            normalizedTags: metadata.tags.map(normalizeSearch),
            notes: metadata.notes,
            headRevision: input.revision,
            createdAt: existing?.createdAt ?? createdAt,
            updatedAt: createdAt,
            archived: existing?.archived ?? false,
            ...(metadata.thumbnailAssetId === undefined ? {} : { thumbnailAssetId: metadata.thumbnailAssetId }),
            ...(existing?.lastOpenedAt === undefined ? {} : { lastOpenedAt: existing.lastOpenedAt }),
          };
          await transaction.put("projects", input.projectId, project);
          await transaction.put("save-attempts", attempt.id, { ...attempt, status: "complete", completedAt: createdAt });
          return { project, checkpoint, assets: resolvedAssets };
        },
      );
    } catch (error) {
      try {
        await this.#adapter.transaction(["save-attempts"], "readwrite", (transaction) => transaction.put("save-attempts", attempt.id,
          { ...attempt, status: "failed", failure: classifySaveFailure(error) }));
      } catch { /* Keep the durable writing marker if even failure metadata cannot be persisted. */ }
      throw error;
    }
  }

  async loadProject(projectId: string): Promise<ProjectSnapshot | undefined> {
    const { report, assets } = await this.#recoveryState(projectId);
    if (!report.project) return undefined;
    const selected = report.candidates.find((candidate) => candidate.valid);
    if (!selected) throw new StorageIntegrityError(`Project ${projectId} has no intact checkpoint`);
    return { checkpoint: selected.checkpoint, assets: assets.filter((asset) => selected.checkpoint.assetIds.includes(asset.id)), project: report.project,
      ...(selected.checkpoint.revision === report.project.headRevision ? {} : { recoveredFromRevision: report.project.headRevision }) };
  }

  async inspectRecovery(projectId: string): Promise<RecoveryReport> {
    return (await this.#recoveryState(projectId)).report;
  }

  async #recoveryState(projectId: string): Promise<{ report: RecoveryReport; assets: StoredAsset[] }> {
    assertId(projectId, "projectId");
    const stored = await this.#adapter.transaction(["projects", "checkpoints", "assets", "save-attempts"], "readonly", async (tx) => ({
      project: await tx.get<ProjectRecord>("projects", projectId),
      checkpoints: (await tx.getAll<DocumentCheckpoint>("checkpoints")).filter((item) => item.projectId === projectId),
      assets: await tx.getAll<StoredAsset>("assets"),
      attempts: (await tx.getAll<SaveAttempt>("save-attempts")).filter((item) => item.projectId === projectId),
    }));
    const candidates: RecoveryCandidate[] = [];
    for (const checkpoint of stored.checkpoints.sort((a, b) => b.revision - a.revision)) {
      const issues = await checkpointIssues(checkpoint, stored.assets, this.#limits);
      if (!stored.project || checkpoint.revision > stored.project.headRevision) issues.push("Checkpoint is beyond the acknowledged head");
      candidates.push({ checkpoint, valid: issues.length === 0, issues });
    }
    const selected = candidates.find((candidate) => candidate.valid);
    const attempts = stored.attempts.sort((a, b) => b.revision - a.revision || a.id.localeCompare(b.id));
    const unrecoverableRevisions = [...new Set([
      ...attempts.filter((attempt) => !candidates.some((candidate) => candidate.valid && candidate.checkpoint.revision === attempt.revision && candidate.checkpoint.documentHash === attempt.documentHash)).map((attempt) => attempt.revision),
      ...candidates.filter((candidate) => !candidate.valid).map((candidate) => candidate.checkpoint.revision),
    ])].sort((a, b) => b - a);
    return { assets: stored.assets, report: { candidates, attempts, unrecoverableRevisions, ...(stored.project ? { project: stored.project } : {}), ...(selected ? { selectedRevision: selected.checkpoint.revision } : {}) } };
  }

  async #readCheckpoint(projectId: string, revision: number): Promise<ProjectSnapshot> {
    return this.loadCheckpoint(projectId, checkpointKey(projectId, revision));
  }

  /** Load one immutable checkpoint without changing the acknowledged project head. */
  async loadCheckpoint(projectId: string, checkpointId: string): Promise<ProjectSnapshot> {
    assertId(projectId, "projectId");
    if (typeof checkpointId !== "string" || !checkpointId.startsWith(`${projectId}:`) || !/:\d{16}$/.test(checkpointId)) {
      throw new StorageValidationError("checkpointId is invalid");
    }
    const stored = await this.#adapter.transaction(["projects", "checkpoints", "assets"], "readonly", async (tx) => ({
      project: await tx.get<ProjectRecord>("projects", projectId),
      checkpoint: await tx.get<DocumentCheckpoint>("checkpoints", checkpointId),
      assets: await tx.getAll<StoredAsset>("assets"),
    }));
    if (!stored.project || !stored.checkpoint || stored.checkpoint.projectId !== projectId) {
      throw new StorageValidationError("Unknown checkpoint");
    }
    const issues = await checkpointIssues(stored.checkpoint, stored.assets, this.#limits);
    if (issues.length) throw new StorageIntegrityError(issues.join("; "));
    return { project: stored.project, checkpoint: stored.checkpoint, assets: stored.assets.filter((asset) => stored.checkpoint!.assetIds.includes(asset.id)) };
  }

  async createNamedCheckpoint(projectId: string, input: { id: string; name: string; revision?: number; purpose?: NamedCheckpoint["purpose"]; now?: Date }): Promise<NamedCheckpoint> {
    assertId(projectId, "projectId");
    assertId(input.id, "checkpoint.id");
    const name = validateCheckpointName(input.name);
    if (input.purpose !== undefined && !["named", "before-restore", "before-flatten"].includes(input.purpose)) throw new StorageValidationError("Invalid checkpoint purpose");
    const snapshot = input.revision === undefined ? await this.loadProject(projectId) : await this.#readCheckpoint(projectId, input.revision);
    if (!snapshot) throw new StorageValidationError("Unknown project");
    const named: NamedCheckpoint = { id: input.id, name, projectId, createdAt: (input.now ?? new Date()).toISOString(), purpose: input.purpose ?? "named", checkpoint: snapshot.checkpoint,
      ...(snapshot.project.thumbnailAssetId ? { thumbnailAssetId: snapshot.project.thumbnailAssetId } : {}) };
    await this.#adapter.transaction(["projects", "checkpoints", "named-checkpoints"], "readwrite", async (tx) => {
      const project = await tx.get<ProjectRecord>("projects", projectId);
      const checkpoint = await tx.get<DocumentCheckpoint>("checkpoints", snapshot.checkpoint.key);
      if (!project || project.deletedAt || checkpoint?.documentHash !== snapshot.checkpoint.documentHash) throw new StorageConflictError("Project changed before checkpoint creation");
      await putNamedCheckpoint(tx, named);
    });
    return structuredClone(named);
  }

  async listNamedCheckpoints(projectId: string): Promise<NamedCheckpoint[]> {
    assertId(projectId, "projectId");
    return this.#adapter.transaction(["named-checkpoints"], "readonly", async (tx) =>
      (await tx.getAll<NamedCheckpoint>("named-checkpoints")).filter((item) => item.projectId === projectId)
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt) || a.id.localeCompare(b.id)));
  }

  /** Resolve the bytes needed to preview or export a named version without mutating the live head. */
  async loadNamedCheckpoint(projectId: string, id: string): Promise<ProjectSnapshot> {
    assertId(projectId, "projectId");
    assertId(id, "checkpoint.id");
    const named = (await this.listNamedCheckpoints(projectId)).find((item) => item.id === id);
    if (!named) throw new StorageValidationError("Unknown named checkpoint");
    const snapshot = await this.loadCheckpoint(projectId, named.checkpoint.key);
    if (snapshot.checkpoint.documentHash !== named.checkpoint.documentHash) {
      throw new StorageIntegrityError("Named checkpoint no longer matches its immutable revision");
    }
    return snapshot;
  }

  /** Deleting a bookmark never removes revision history or its referenced assets. */
  async deleteNamedCheckpoint(projectId: string, id: string): Promise<void> {
    assertId(projectId, "projectId"); assertId(id, "checkpoint.id");
    await this.#adapter.transaction(["named-checkpoints"], "readwrite", (tx) => tx.delete("named-checkpoints", namedKey(projectId, id)));
  }

  async restoreNamedCheckpoint(projectId: string, id: string, options: { expectedHeadRevision: number; preservationId: string; now?: Date }): Promise<ProjectSnapshot> {
    assertId(projectId, "projectId"); assertId(id, "checkpoint.id"); assertId(options.preservationId, "preservationId");
    const named = (await this.listNamedCheckpoints(projectId)).find((item) => item.id === id);
    if (!named) throw new StorageValidationError("Unknown named checkpoint");
    return this.#restoreSnapshot(projectId, named.checkpoint, named.name, options);
  }

  /** Restore any intact immutable checkpoint as a new head; later revisions remain available. */
  async restoreCheckpoint(projectId: string, checkpointId: string, options: { expectedHeadRevision: number; preservationId: string; now?: Date }): Promise<ProjectSnapshot> {
    assertId(projectId, "projectId"); assertId(options.preservationId, "preservationId");
    const selected = await this.loadCheckpoint(projectId, checkpointId);
    return this.#restoreSnapshot(projectId, selected.checkpoint, `revision ${selected.checkpoint.revision}`, options);
  }

  async #restoreSnapshot(projectId: string, checkpoint: DocumentCheckpoint, label: string, options: { expectedHeadRevision: number; preservationId: string; now?: Date }): Promise<ProjectSnapshot> {
    const current = await this.loadProject(projectId);
    if (!current || current.project.headRevision !== options.expectedHeadRevision || current.checkpoint.revision !== current.project.headRevision) throw new StorageConflictError("Current head changed or requires recovery before restore");
    const assets = await this.#adapter.transaction(["assets"], "readonly", (tx) => tx.getAll<StoredAsset>("assets"));
    const issues = await checkpointIssues(checkpoint, assets, this.#limits);
    if (issues.length) throw new StorageIntegrityError(issues.join("; "));
    const revision = current.project.headRevision + 1;
    const document = structuredClone(checkpoint.document);
    if (typeof document.revision === "number") document.revision = revision;
    const now = options.now ?? new Date();
    const preserved: NamedCheckpoint = { id: options.preservationId, projectId, name: `Before restoring ${label}`.slice(0, MAX_CHECKPOINT_NAME_LENGTH),
      purpose: "before-restore", createdAt: now.toISOString(), checkpoint: current.checkpoint };
    return this.#saveCheckpoint({ projectId, revision, expectedHeadRevision: options.expectedHeadRevision, metadata: current.project, document,
      assetIds: checkpoint.assetIds, now }, preserved);
  }

  /** Save the caller's divergent in-memory document to a new identity; the original head is never touched. */
  async saveConflictCopy(input: SaveCheckpointInput, copyProjectId: string): Promise<ProjectSnapshot> {
    assertId(copyProjectId, "copyProjectId");
    if (copyProjectId === input.projectId) throw new StorageConflictError("A conflict copy requires a new project ID");
    const document = structuredClone(input.document);
    document.id = copyProjectId;
    if (typeof document.revision === "number") document.revision = 1;
    const title = `${input.metadata.title.slice(0, this.#limits.maxTitleLength - 7)} (copy)`;
    return this.saveCheckpoint({ projectId: copyProjectId, revision: 1, createOnly: true, metadata: { ...input.metadata, title }, document,
      assetIds: input.assetIds, ...(input.assets ? { assets: input.assets } : {}), ...(input.now ? { now: input.now } : {}) });
  }

  async listProjects(query: LibraryQuery = {}): Promise<ProjectRecord[]> {
    const archived = query.archived ?? false;
    const needle = normalizeSearch(query.text?.trim() ?? "");
    const projects = await this.#adapter.transaction(["projects"], "readonly", (transaction) =>
      transaction.getAll<ProjectRecord>("projects"),
    );
    const filtered = projects.filter(
      (project) =>
        Boolean(project.deletedAt) === (query.deleted ?? false) &&
        (query.deleted === true || project.archived === archived) &&
        (needle.length === 0 || project.normalizedTitle.includes(needle) || project.normalizedTags.some((tag) => tag.includes(needle))),
    );
    return filtered.sort(
      query.sort === "alphabetical"
        ? (a, b) => a.title.localeCompare(b.title) || a.id.localeCompare(b.id)
        : (a, b) =>
            (b.lastOpenedAt ?? b.updatedAt).localeCompare(a.lastOpenedAt ?? a.updatedAt) || a.id.localeCompare(b.id),
    );
  }

  async updateMetadata(projectId: string, metadata: ProjectMetadataInput, now = new Date()): Promise<ProjectRecord> {
    assertId(projectId, "projectId");
    const validated = validateMetadata(metadata, this.#limits);
    return this.#adapter.transaction(["projects"], "readwrite", async (transaction) => {
      const project = await transaction.get<ProjectRecord>("projects", projectId);
      if (!project) throw new StorageValidationError(`Unknown project: ${projectId}`);
      const updated: ProjectRecord = {
        ...project,
        title: validated.title,
        normalizedTitle: normalizeSearch(validated.title),
        tags: [...validated.tags],
        normalizedTags: validated.tags.map(normalizeSearch),
        notes: validated.notes,
        updatedAt: now.toISOString(),
        ...(validated.thumbnailAssetId === undefined ? {} : { thumbnailAssetId: validated.thumbnailAssetId }),
      };
      if (validated.thumbnailAssetId === undefined) delete updated.thumbnailAssetId;
      await transaction.put("projects", projectId, updated);
      return updated;
    });
  }

  async setArchived(projectId: string, archived: boolean, now = new Date()): Promise<void> {
    await this.#updateProject(projectId, (project) => ({ ...project, archived, updatedAt: now.toISOString() }));
  }

  /** Delete is recoverable across browser restarts until explicitly purged. */
  async setDeleted(projectId: string, deleted: boolean, now = new Date()): Promise<void> {
    await this.#updateProject(projectId, (project) => {
      const updated = { ...project, updatedAt: now.toISOString() };
      if (deleted) updated.deletedAt = now.toISOString();
      else delete updated.deletedAt;
      return updated;
    });
  }

  /** Purge exactly one trashed project, retaining assets used by any surviving checkpoint. */
  async purgeProject(projectId: string): Promise<void> {
    assertId(projectId, "projectId");
    await this.#adapter.transaction(["projects", "checkpoints", "assets", "named-checkpoints", "save-attempts"], "readwrite", async (transaction) => {
      const project = await transaction.get<ProjectRecord>("projects", projectId);
      if (!project?.deletedAt) throw new StorageValidationError("Only a project in Trash can be permanently deleted");
      const checkpoints = await transaction.getAll<DocumentCheckpoint>("checkpoints");
      const projects = await transaction.getAll<ProjectRecord>("projects");
      const named = await transaction.getAll<NamedCheckpoint>("named-checkpoints");
      const retained = new Set(checkpoints.filter((item) => item.projectId !== projectId).flatMap((item) => item.assetIds));
      for (const item of named) {
        if (item.projectId === projectId) await transaction.delete("named-checkpoints", namedKey(projectId, item.id));
        else { for (const id of item.checkpoint.assetIds) retained.add(id); if (item.thumbnailAssetId) retained.add(item.thumbnailAssetId); }
      }
      for (const attempt of await transaction.getAll<SaveAttempt>("save-attempts")) if (attempt.projectId === projectId) await transaction.delete("save-attempts", attempt.id);
      for (const item of projects) if (item.id !== projectId && item.thumbnailAssetId) retained.add(item.thumbnailAssetId);
      for (const checkpoint of checkpoints) if (checkpoint.projectId === projectId) await transaction.delete("checkpoints", checkpoint.key);
      for (const asset of await transaction.getAll<StoredAsset>("assets")) if (!retained.has(asset.id)) await transaction.delete("assets", asset.id);
      await transaction.delete("projects", projectId);
    });
  }

  async markOpened(projectId: string, now = new Date()): Promise<void> {
    await this.#updateProject(projectId, (project) => {
      if (project.deletedAt || project.archived) throw new StorageValidationError("Only active projects can be marked opened");
      return { ...project, lastOpenedAt: now.toISOString() };
    });
  }

  async recentProjects(limit = 10): Promise<ProjectRecord[]> {
    if (!Number.isSafeInteger(limit) || limit < 1 || limit > 100) throw new StorageValidationError("Recent limit must be between 1 and 100");
    return (await this.listProjects({ sort: "recent" })).filter((project) => project.lastOpenedAt !== undefined).slice(0, limit);
  }

  async resumeLastProject(): Promise<ProjectSnapshot | undefined> {
    for (const project of await this.recentProjects(100)) {
      try { const snapshot = await this.loadProject(project.id); if (snapshot) return snapshot; }
      catch (error) { if (!(error instanceof StorageIntegrityError)) throw error; }
    }
    return undefined;
  }

  async clearRecentHistory(): Promise<void> {
    await this.#adapter.transaction(["projects"], "readwrite", async (tx) => {
      for (const project of await tx.getAll<ProjectRecord>("projects")) {
        if (project.lastOpenedAt !== undefined) { delete project.lastOpenedAt; await tx.put("projects", project.id, project); }
      }
    });
  }

  /** Plain text metadata only; never inserts markup or changes the artwork document/hash. */
  async updateNotes(projectId: string, notes: string, now = new Date()): Promise<void> {
    if (typeof notes !== "string" || notes.length > this.#limits.maxNotesLength) throw new StorageValidationError(`notes may contain at most ${this.#limits.maxNotesLength} characters`);
    await this.#updateProject(projectId, (project) => ({ ...project, notes, updatedAt: now.toISOString() }));
  }

  async #updateProject(projectId: string, update: (project: ProjectRecord) => ProjectRecord): Promise<void> {
    assertId(projectId, "projectId");
    await this.#adapter.transaction(["projects"], "readwrite", async (transaction) => {
      const project = await transaction.get<ProjectRecord>("projects", projectId);
      if (!project) throw new StorageValidationError(`Unknown project: ${projectId}`);
      await transaction.put("projects", projectId, update(project));
    });
  }

  async #prepareAsset(asset: AssetInput, now = new Date()): Promise<StoredAsset> {
    assertId(asset.id, "asset.id");
    if (typeof asset.mediaType !== "string" || asset.mediaType.length < 3 || asset.mediaType.length > 127) {
      throw new StorageValidationError("asset.mediaType is invalid");
    }
    if (!isUint8Array(asset.bytes) || asset.bytes.byteLength > this.#limits.maxAssetBytes) {
      throw new StorageValidationError(`Asset exceeds ${this.#limits.maxAssetBytes} bytes`);
    }
    const bytes = new Uint8Array(asset.bytes);
    const digest = await sha256(bytes);
    if (asset.sha256 !== undefined && asset.sha256 !== digest) {
      throw new StorageIntegrityError(`Asset ${asset.id} does not match its declared hash`);
    }
    return {
      id: asset.id,
      mediaType: asset.mediaType,
      byteLength: bytes.byteLength,
      sha256: digest,
      bytes,
      createdAt: now.toISOString(),
    };
  }
}

function validateCheckpointName(name: string): string {
  if (typeof name !== "string" || !name.trim() || name.length > MAX_CHECKPOINT_NAME_LENGTH) throw new StorageValidationError(`Checkpoint name must contain 1-${MAX_CHECKPOINT_NAME_LENGTH} characters`);
  return name.trim();
}

const namedKey = (projectId: string, id: string): string => JSON.stringify([projectId, id]);

async function putNamedCheckpoint(tx: StorageTransaction, checkpoint: NamedCheckpoint): Promise<void> {
  const key = namedKey(checkpoint.projectId, checkpoint.id);
  if (await tx.get("named-checkpoints", key)) throw new StorageConflictError("Named checkpoint ID already exists");
  await tx.put("named-checkpoints", key, checkpoint);
}

async function checkpointIssues(checkpoint: DocumentCheckpoint, assets: StoredAsset[], limits: StorageLimits): Promise<string[]> {
  const issues: string[] = [];
  try {
    if (checkpoint.complete !== true || !Number.isSafeInteger(checkpoint.revision) || checkpoint.revision < 1 || checkpoint.key !== checkpointKey(checkpoint.projectId, checkpoint.revision)) issues.push("Incomplete or invalid checkpoint metadata");
    validateJsonDocument(checkpoint.document, limits);
    if (await sha256Json(checkpoint.document) !== checkpoint.documentHash) issues.push("Document hash mismatch");
    if (!Array.isArray(checkpoint.assetIds) || checkpoint.assetIds.length > limits.maxAssetsPerProject || new Set(checkpoint.assetIds).size !== checkpoint.assetIds.length) throw new StorageIntegrityError("Invalid asset references");
    for (const id of checkpoint.assetIds) {
      assertId(id, "checkpoint.assetId");
      const asset = assets.find((item) => item.id === id);
      if (!asset) issues.push(`Missing asset: ${id}`);
      else if (!isUint8Array(asset.bytes) || asset.bytes.byteLength !== asset.byteLength || await sha256(asset.bytes) !== asset.sha256) issues.push(`Damaged asset: ${id}`);
    }
  } catch (error) { issues.push(error instanceof Error ? error.message : "Malformed checkpoint"); }
  return issues;
}
